import React, { useEffect, useState } from 'react';
import { Button, Tabs, Tooltip } from 'freemium-ui';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import { History } from 'history';
import EmptyState from 'shared/emptyState';
import { GoToRouteType, SurveyType, TemplatesGalleryType } from 'shared/types';
import Icons from 'components/icons';
import Spacer from 'components/Spacer';
import { useAccountInfo } from 'shared/hooks';

import { OnboardingSetupWizard } from '../style';
import SurveyPreview from '../../../../builder/survey-options-menu/SurveyPreview';
import QuestionPreview from '../../../../builder/questions-preview';
import {
  useTemplatesGallery,
  useSavedTemplates,
  useGetTemplateById,
} from '../../../../../../template/hooks';
import { Loader } from 'freemium-ui';
import { Center } from 'shared/globals';
import { useQueryClient } from 'react-query';
import { isNil } from 'lodash';

export type TemplateSelectorProps = {
  isCreateTemplate?: boolean;
  isLoading: boolean;
  isOnboarding: boolean;
  goBack?: Function;
  onTemplateSelection: Function;
  accountInfo?: any;
  goToRoute: GoToRouteType;
};

type OptionIdType = 'templates_gallery' | 'saved_templates';

type OptionType = {
  id: OptionIdType;
  name: string;
};

const options: OptionType[] = [
  {
    id: 'templates_gallery',
    name: 'Templates gallery',
  },
  {
    id: 'saved_templates',
    name: 'Saved templates',
  },
];

export const TemplateSelector = ({
  isOnboarding = true,
  isCreateTemplate = false,
  goBack,
  isLoading,
  onTemplateSelection,
  goToRoute,
}: TemplateSelectorProps) => {
  const history: History = useHistory();
  const queryClient = useQueryClient();

  const [activeTemplateIndex, setActiveTemplateIndex] = useState(0);
  const [mobile, setMobile] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [surveyRestartCount, setSurveyRestartCount] = useState<number>(0); // counting it just to set the key to incrementing number so that it would remount
  const [filteredTemplates, setFilteredTemplates] = useState<SurveyType[]>([]);
  const [fetchTemplateId, setFetchTemplateId] = useState<string>();
  const [activeTemplate, setActiveTemplate] = useState<SurveyType>({});
  const [activeTab, setActiveTab] = useState<OptionIdType>('templates_gallery');

  const { data: galleryTemplates, isLoading: isLoadingGallery } =
    useTemplatesGallery({});
  const { data: savedTemplates, isLoading: isLoadingSavedTemplates } =
    useSavedTemplates();
  const { isLoading: isTemplateLoading } = useGetTemplateById({
    templateId: fetchTemplateId || '',
    callback: (isSuccess: boolean, data: SurveyType) => {
      if (isSuccess && data?.id) {
        setActiveTemplate(data);
        setFetchTemplateId('');
      }
    },
  });
  const { data: accountInfo } = useAccountInfo({});

  const restartPreview = () => {
    setSurveyRestartCount(surveyRestartCount + 1);
  };

  useEffect(() => {
    if (filteredTemplates[activeTemplateIndex]) {
      const actTemplate = filteredTemplates[activeTemplateIndex];
      if (actTemplate.id) {
        const cacheData: SurveyType | undefined = queryClient.getQueryData([
          'templates',
          actTemplate.id,
        ]);
        if (cacheData?.id) {
          setActiveTemplate(cacheData);
        } else {
          setFetchTemplateId(actTemplate.id);
        }
      } else {
        setActiveTemplate(actTemplate);
      }
    }
  }, [activeTemplateIndex, filteredTemplates]);

  useEffect(() => {
    if (activeTab === 'templates_gallery') {
      const filtered = (galleryTemplates || [])
        .filter((tempGallery) => !isNil(tempGallery?.survey))
        .map((tempGallery) => tempGallery?.survey);
      setFilteredTemplates(filtered);
      setActiveTemplateIndex(0);
      return;
    }
    if (activeTab === 'saved_templates') {
      setFilteredTemplates(savedTemplates || []);
      setActiveTemplateIndex(0);
      return;
    }
  }, [activeTab, galleryTemplates, savedTemplates]);

  return (
    <StyledTemplateSelector>
      <OnboardingSetupWizard>
        <div className="setup-content" style={{ height: '720px' }}>
          <div className="setup-content-header">
            <div className="title">
              {`Let’s start creating a ${
                isCreateTemplate ? 'template' : 'survey'
              }`}
              &nbsp;
              {/* <Tooltip
              label="Use our pre-configured templates to speed up survey creation"
              position="left"
            >
              <Icons glyph="info" size="18" viewBox="0 0 10 10" />
            </Tooltip> */}
            </div>
            <div className="subtitle">
              Based on your target audience and your industry, pick an NPS
              template to speed up your survey creation.
            </div>
            {!isOnboarding && (
              <>
                <Tabs
                  value={activeTab}
                  onChange={(e: any, v: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveTab(v.id);
                  }}
                  options={options}
                />
                <br />
              </>
            )}
            {/* {activeTab === 'templates_gallery' && (
              <div className="tags">
                {filters.map((filter: string) => (
                  <div
                    className={`tag ${filter === activeFilter && 'active'}`}
                    onClick={() => setActiveFilter(filter)}
                    key={filter}
                  >
                    {filter} <span>{`(${templatesCount[filter] ?? 0})`}</span>
                  </div>
                ))}
              </div>
            )} */}
          </div>
          <div className="setup-content-body">
            {isLoadingGallery || isLoadingSavedTemplates ? (
              Array(4)
                .fill(0)
                .map((_, index) => (
                  <React.Fragment key={index}>
                    <Skeleton
                      height={105}
                      width={370}
                      style={{ background: '#ebeff3' }}
                    />
                    <Spacer y={0.5} />
                  </React.Fragment>
                ))
            ) : (
              <div className="templates">
                {filteredTemplates.length ? (
                  filteredTemplates.map((template: any, index: any) => (
                    <button
                      className={`template ${
                        index === activeTemplateIndex && 'active'
                      }`}
                      key={index}
                      onClick={() => setActiveTemplateIndex(index)}
                    >
                      {activeTab === 'templates_gallery' &&
                        galleryTemplates &&
                        galleryTemplates?.[index]?.popular && (
                          <div className="popular-label">Most Popular</div>
                        )}
                      <div className="name">{template.name}</div>
                      <div className="desc">{template.desc}</div>
                    </button>
                  ))
                ) : activeTab === 'saved_templates' ? (
                  <div className="no-templates-info">No saved templates</div>
                ) : (
                  Array(4)
                    .fill(0)
                    .map((_, index) => (
                      <React.Fragment key={index}>
                        <Skeleton
                          height={105}
                          width={370}
                          style={{ background: '#ebeff3' }}
                        />
                      </React.Fragment>
                    ))
                )}
              </div>
            )}
          </div>
          {filteredTemplates?.length ? (
            <div className="setup-content-actions">
              <div className="actions-secondary">
                {isOnboarding ? (
                  <Button
                    inline
                    disabled={isLoading}
                    type="secondary"
                    onClick={() => {
                      goBack?.();
                    }}
                  >
                    Back
                  </Button>
                ) : null}
              </div>
              <div className="actions-primary">
                <Button
                  inline
                  disabled={isLoading || isTemplateLoading}
                  onClick={() => onTemplateSelection(activeTemplate)}
                  data-testid="select-template-button"
                >
                  Apply this template
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        {isTemplateLoading ? (
          <div className="setup-content-preview">
            {!isOnboarding ? (
              <div
                className="close"
                onClick={() => {
                  goToRoute({ path: '/survey', history });
                }}
              >
                <Icons glyph="close-mini" size="18" viewBox="0 0 14 14" />
              </div>
            ) : null}
            <Center>
              <Loader />
            </Center>
          </div>
        ) : (
          <div className="setup-content-preview">
            {!isOnboarding ? (
              <div
                className="close"
                onClick={() => {
                  goToRoute({ path: '/survey', history });
                }}
              >
                <Icons glyph="close-mini" size="18" viewBox="0 0 14 14" />
              </div>
            ) : null}
            {filteredTemplates?.length === 0 &&
            activeTab === 'saved_templates' ? (
              <EmptyState
                imgSrc="empty-state-survey.svg"
                title="No saved templates here!"
                width="600px"
                height="400px"
              >
                <div className="empty-state-desc">
                  <div className="icon-wrapper">
                    <Icons glyph="info" size={16} viewBox="0 0 16 16" />
                  </div>
                  <div>
                    You can save the survey with its questions, branching logics
                    and design as a template from the 'Survey builder' section
                    by selecting 'Save as a template' option.
                  </div>
                </div>
              </EmptyState>
            ) : (
              <>
                <div className="actions">
                  <div className="controls">
                    <div
                      className={`control ${showPreview ? 'active' : ''}`}
                      onClick={() => {
                        setShowPreview(true);
                      }}
                    >
                      Live demo
                    </div>
                    <div
                      className={`control ${showPreview ? '' : 'active'}`}
                      onClick={() => {
                        setShowPreview(false);
                      }}
                    >
                      Show all questions
                    </div>
                  </div>
                  {showPreview && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                      }}
                    >
                      <div className="controls">
                        <Tooltip label="Desktop view" position="bottom">
                          <div
                            className={`control ${mobile ? '' : 'active'}`}
                            onClick={() => setMobile?.(false)}
                          >
                            <Icons
                              glyph="desktop"
                              size="14"
                              viewBox="0 0 16 14"
                            />
                          </div>
                        </Tooltip>
                        <Tooltip label="Mobile view" position="bottom">
                          <div
                            className={`control ${mobile ? 'active' : ''}`}
                            onClick={() => setMobile?.(true)}
                          >
                            <Icons
                              glyph="mobile"
                              size="14"
                              viewBox="0 0 8 14"
                            />
                          </div>
                        </Tooltip>
                      </div>
                      <div className="controls">
                        <Tooltip label="Restart preview" position="bottom">
                          <div
                            className={`control active`}
                            onClick={restartPreview}
                          >
                            <Icons
                              glyph="restart"
                              size="15"
                              viewBox="0 0 15 12"
                            />
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                </div>
                {showPreview && activeTemplate && (
                  <>
                    <div className={`preview ${mobile ? 'mobile' : ''}`}>
                      <SurveyPreview
                        placeholdersValues={{
                          '{{account.brand_color}}': accountInfo?.brand_color,
                          '{{account.company_name}}': accountInfo?.company_name,
                          '{{account.id}}': accountInfo?.id,
                          '{{account.logo_url}}': accountInfo?.company_name,
                        }}
                        close={restartPreview}
                        values={activeTemplate}
                        accountInfo={accountInfo}
                        mobile={mobile}
                        key={
                          activeTemplate.name +
                          String(mobile) +
                          String(surveyRestartCount)
                        }
                      />
                    </div>
                    <div className="preview-info">
                      <Icons glyph="info" size="18" viewBox="0 0 16 16" />{' '}
                      &nbsp;
                      <span>
                        This is a preview, your response in this screen will not
                        be recorded.
                      </span>
                    </div>
                  </>
                )}
                {!showPreview && (
                  <QuestionPreview
                    surveyForm={activeTemplate}
                    accountInfo={accountInfo}
                  />
                )}
              </>
            )}
          </div>
        )}
      </OnboardingSetupWizard>
    </StyledTemplateSelector>
  );
};

const StyledTemplateSelector = styled.div`
  [role='tablist'] {
    overflow: unset;
    border-bottom: 1px solid #ededed;
  }
  .tags {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }
  .tag {
    border: 1px solid #cfd7df;
    font-size: 12px;
    border-radius: 4px;
    font-weight: 500;
    padding: 2px 4px;
    // text-transform: capitalize;
    cursor: pointer;
    span {
      font-weight: 400;
    }
    &.active {
      color: #2c5cc5;
      background: #e5f2fd;
      border: 1px solid #bbdcfe;
    }
  }
  .setup-content-preview {
    width: fill-available;
    position: relative;
    overflow: auto;
    border-radius: 4px;
    background: #eaeff3;
    .close {
      position: fixed;
      top: 8px;
      right: 15px;
      cursor: pointer;
      z-index: 333;
    }
  }
  .templates {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .template {
    border-radius: 3px;
    background-color: transparent;
    cursor: pointer;
    position: relative;
    text-align: left;
    border-radius: 3px;
    padding: 12px 15px;
    height: auto;
    min-height: fit-content;
    display: block;
    padding: 20px 20px;
    border-radius: 4px;
    margin-bottom: 5px;
    transition: box-shadow 150ms linear 0s;
    box-shadow: var(--shadow-default);

    &.active {
      box-shadow: none;
      border: 1px solid var(--color-azure-800);
      box-shadow: 0px 6px 14px rgba(24, 50, 71, 0.08);
      .name {
        color: var(--color-azure-800);
        font-weight: 600;
      }
    }

    &:hover {
      box-shadow: var(--shadow-default-hover);
    }

    // &:focus,
    // &.active {
    //   border: 2px solid var(--color-smoke-300);
    //   box-shadow: 0px 6px 14px rgba(24, 50, 71, 0.08);
    // }
  }
  .setup-content-header {
    .title {
      font-size: 18px;
    }
    .subtitle {
      font-size: 14px;
      font-weight: 500;
      padding-top: 6px;
      display: block;
      line-height: 22px;
    }
  }

  .popular-label {
    font-weight: 600;
    color: #00795b;
    padding: 5px 8px;
    background: #e0f5f1;
    border: 1px solid #b4e5da;
    border-radius: 4px;
    float: right;
    display: inline;
    font-size: 11px;
    right: 10px;
    line-height: 1;
  }
  .name {
    font-weight: 500;
    font-size: 15px;
    line-height: 20px;
    margin-bottom: 4px;
    word-break: break-word;
  }
  .desc {
    font-size: 13px;
    color: var(--color-smoke-800);
    line-height: 20px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }

  .actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 28px;
    line-height: 1rem;
    font-weight: 600;
    font-size: 13px;
    height: 40px;
    color: #4c4c4c;
    padding-top: 52px;
    position: sticky;
    top: 0;
    z-index: 100;
    background: #eaeff3;
  }
  .controls {
    padding: 8px 0;
    display: flex;
    align-items: center;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0px 2px 6px rgb(18 52 77 / 10%);
    [data-tooltipped] {
      &:first-of-type:not(:only-of-type) {
        border-right: 1px solid #ededed;
      }
    }
  }
  .control {
    padding: 3px 12px;
    line-height: 18px;
    display: flex;
    opacity: 0.6;
    cursor: pointer;
    &:first-of-type:not(:only-of-type) {
      border-right: 1px solid #ededed;
    }
    &.active {
      opacity: 1;
    }
  }
  .preview-info {
    position: absolute;
    bottom: 8px;
    z-index: 1;
    transform: translateX(-50%);
    left: 50%;
    padding: 8px 16px 8px 6px;
    background: #e0e6ec;
    border-radius: 4px;
    margin-bottom: 12px;
    border: none;
    font-style: italic;
    font-size: 14px;
    line-height: 20px;
    color: #12344d;
    display: flex;
    align-items: center;
    width: fit-content;
    white-space: nowrap;
    span {
      border-left: 1px solid #bebebe;
      padding-left: 12px;
    }
  }
  .empty-state-desc {
    font-size: 14px;
    line-height: 22px;
    color: #666;
    padding: 8px 12px;
    text-align: left;
    display: flex;
    justify-content: center;
    border-radius: 4px;
    background: rgb(229, 242, 253);
    border: 1px solid rgb(187, 220, 254);
    .icon-wrapper {
      margin-right: 20px;
      border-right: 1px solid #ddd;
      padding-right: 12px;
    }
  }
  .no-templates-info {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: rgb(145 156 165);
  }
  .preview {
    --background-color: #ebeff3;
    position: relative;
    flex-grow: 1;
    zoom: 0.9;
    // max-width: 800px;
    &.mobile {
      height: 600px;
      width: 300px;
      background-image: url('/mobile-skeleton.png');
      background-repeat: no-repeat;
      margin: 40px 0;
      overflow: auto;
      .survey-previewl {
        max-height: 90%;
      }
      & > div {
        width: 342px;
        zoom: 0.75;
        margin-left: 11px;
        margin-top: 48px;
        .main {
          padding: 0;
        }
      }
    }
    //hide progressbar ,close button & toggle preview
    .progressbar-container,
    .preview-footer,
    .toggle-preview,
    .close {
      display: none !important ;
    }
  }
`;

export default TemplateSelector;
